import { useEffect, useMemo } from 'react';
import { useSitesStore } from '@/features/sites/store';
import { useProjectsStore } from '@/features/projects/store';

interface UseSiteDataProps {
  siteId: number;
  projectId: number;
}

export const useSiteData = ({ siteId, projectId }: UseSiteDataProps) => {
  // Zustand stores
  const { currentProject: project } = useProjectsStore();
  const { getProject } = useProjectsStore(state => state.actions);

  const { sites, materials, siteMaterials, photos, loading } = useSitesStore();

  const {
    getSites,
    getMaterialsList,
    getSiteMaterials,
    updateSite,
    updateSiteMaterials,
    deleteSiteMaterials,
    getCompressedPhotos,
    deletePhoto,
    clearSiteMaterials,
    isMaterialsLoading,
    isPhotosLoading,
  } = useSitesStore(state => state.actions);

  // Initialize data with parallel fetching for performance
  const initialData = async (siteId: number) => {
    try {
      // Prepare all independent data fetch promises
      const promises = [
        getMaterialsList(),
        getSiteMaterials(siteId),
        getCompressedPhotos(siteId),
      ];

      // Add project-related fetches if projectId exists
      if (projectId) {
        promises.push(getSites(projectId), getProject(projectId));
      }

      // Execute all independent requests in parallel
      const results = await Promise.allSettled(promises);
      // Check for any failed requests and log errors
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const requestNames = [
            'getMaterialsList',
            'getSiteMaterials',
            'getCompressedPhotos',
            ...(projectId ? ['getSites', 'getProject'] : []),
          ];
          console.error(
            `Failed to fetch ${requestNames[index]}:`,
            result.reason
          );
        }
      });

      // At least log if critical requests failed
      const criticalFailures = results
        .slice(0, 3) // First 3 are critical for site functionality
        .filter(result => result.status === 'rejected');

      if (criticalFailures.length > 0) {
        console.warn(
          `${criticalFailures.length} critical data fetches failed. Some features may not work properly.`
        );
      }
    } catch (error) {
      console.error('Unexpected error during data initialization:', error);
    }
  };

  // Clear materials when siteId changes to prevent showing old data
  useEffect(() => {
    clearSiteMaterials();
  }, [siteId, clearSiteMaterials]);

  // Load data on mount
  useEffect(() => {
    if (siteId) {
      initialData(siteId).catch(error => {
        console.error('Failed to initialize page data:', error);
        // Note: Individual request errors are already handled in initialData
      });
    }
  }, [siteId, projectId]);

  // Find current site from sites array
  const currentSite = useMemo(() => {
    if (sites.length > 0 && siteId) {
      return sites.find(site => site.id === siteId) || null;
    }
    return null;
  }, [sites, siteId]);

  // Transform photos for PhotoEdit component (normalize API data to camelCase)
  const photoList = useMemo(() => {
    if (photos && photos.length) {
      return photos
        .filter(photo => photo.id !== undefined) // Filter out photos without ID
        .map(photo => {
          return {
            dataUrl: photo.base64Image, // Normalize from API snake_case to camelCase
            id: photo.id!, // Use real photo ID from server, non-null assertion safe after filter
          };
        });
    }
    return [];
  }, [photos]);

  return {
    // State
    project,
    currentSite,
    sites,
    materials,
    siteMaterials,
    photos: photoList,
    loading,
    // Legacy compatibility
    materialsLoading: isMaterialsLoading(),
    photosLoading: isPhotosLoading(),

    // Actions
    actions: {
      updateSite,
      updateSiteMaterials,
      deleteSiteMaterials,
      deletePhoto,
      refreshPhotos: async () => {
        await getCompressedPhotos(siteId);
      },
    },
  };
};
